import type { AuthSuccess } from "@reelio/contracts";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { Navigate, useLocation } from "react-router-dom";

import { getViewer } from "./api";
import { authClient } from "./auth-client";

type AuthStatus = "loading" | "authenticated" | "anonymous";

type AuthContextValue = {
  status: AuthStatus;
  viewer: AuthSuccess | null;
  refresh: () => Promise<AuthSuccess | null>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const {
    data: sessionData,
    isPending: sessionPending,
    refetch: refetchSession,
  } = authClient.useSession();
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [viewer, setViewer] = useState<AuthSuccess | null>(null);

  const refresh = useCallback(async (): Promise<AuthSuccess | null> => {
    await refetchSession();
    const current = await authClient.getSession();

    if (!current.data) {
      setViewer(null);
      setStatus("anonymous");
      return null;
    }

    const nextViewer = await getViewer();
    setViewer(nextViewer);
    setStatus("authenticated");
    return nextViewer;
  }, [refetchSession]);

  useEffect(() => {
    if (sessionPending) {
      setStatus("loading");
      return;
    }

    if (!sessionData) {
      setViewer(null);
      setStatus("anonymous");
      return;
    }

    const controller = new AbortController();
    setStatus("loading");

    void getViewer(controller.signal)
      .then((nextViewer) => {
        setViewer(nextViewer);
        setStatus("authenticated");
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }
        setViewer(null);
        setStatus("anonymous");
      });

    return () => controller.abort();
  }, [sessionData, sessionPending]);

  const signOut = useCallback(async () => {
    try {
      await authClient.signOut();
    } finally {
      setViewer(null);
      setStatus("anonymous");
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      viewer,
      refresh,
      signOut,
    }),
    [refresh, signOut, status, viewer],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const value = useContext(AuthContext);

  if (!value) {
    throw new Error("useAuth must be used within AuthProvider.");
  }

  return value;
};

export const ProtectedRoute = ({ children }: { children: ReactNode }) => {
  const { status } = useAuth();
  const location = useLocation();

  if (status === "loading") {
    return <div className="route-loading">Opening your workspace…</div>;
  }

  if (status === "anonymous") {
    const redirect = `${location.pathname}${location.search}`;
    return (
      <Navigate
        to={`/auth/login?redirect=${encodeURIComponent(redirect)}`}
        replace
      />
    );
  }

  return children;
};
